import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { IAdminPanelForm } from 'components/organisms/AdminPanelForm/AdminPanelForm.schema';
import {
  useLazyGetEntityQuery,
  useLazyGetJobCodeQuery,
  useLazyGetPicklistQuery,
  useLazyGetPositionQuery,
} from 'services/sap';
import { catalogoLenguage, catalogoPersonal } from 'utils/helpers/dictionaries';

export type PositionSyncStatus = 'idle' | 'loading' | 'success' | 'error';

interface CatalogByCode {
  externalCode: string;
  name?: string;
}

interface CatalogByName {
  externalCode: string;
  externalName?: string;
}

interface SeniorityOption {
  externalCode: string;
  label_es_ES?: string;
}

export const PositionSyncMessages = {
  idle: 'Ingresa el ID y completaremos automáticamente la información de los demás campos directamente desde SAP.',
  loading: 'Estamos trayendo la información de la posición. Este proceso puede tardar unos minutos.',
  notFound: 'No encontramos la posición en SAP. Verifica que el ID sea correcto e inténtalo nuevamente.',
  inactive: 'La posición no está activa en SAP. Verifica el ID o usa otra posición.',
  notVacant: 'La posición no está vacante. Para contratar, la posición debe estar disponible en SAP.',
  failed: 'Ocurrió un error al sincronizar la información desde SAP. Inténtalo nuevamente.',
};

function nameByCode(catalog: CatalogByCode[] | undefined, code?: string) {
  if (!catalog || !code) return '';
  return catalog.find((item) => item.externalCode === code)?.name ?? '';
}


function matchByName<T extends { externalCode: string }>(
  catalog: T[] | undefined,
  label: string,
  getLabel: (item: T) => string | undefined
): { label: string; code: string } {
  if (!catalog || !label) return { label: '', code: '' };
  const found = catalog.find(
    (item) => (getLabel(item) ?? '').trim().toLowerCase() === label.trim().toLowerCase()
  );
  return found ? { label: getLabel(found) ?? '', code: found.externalCode } : { label: '', code: '' };
}


export function parseJobTitle(jobTitle: string, seniorityLabels: string[]) {
  const segments = jobTitle
    .split('-')
    .map((segment) => segment.trim())
    .filter(Boolean);

  // El primer segmento es el chapter; el chapter autoritativo viene de jobFunction.
  let rest = segments.slice(1);
  let level = '';

  const last = rest[rest.length - 1];
  const isLevel = last && seniorityLabels.some((l) => l.toLowerCase() === last.toLowerCase());
  if (isLevel) {
    level = last;
    rest = rest.slice(0, -1);
  }

  return { specialty: rest[0] ?? '', technology: rest[1] ?? '', level };
}

export function usePositionSync() {
  const { setValue } = useFormContext<IAdminPanelForm>();
  const [status, setStatus] = useState<PositionSyncStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const [fetchPosition] = useLazyGetPositionQuery();
  const [fetchJobCode] = useLazyGetJobCodeQuery();
  const [fetchEntity] = useLazyGetEntityQuery();
  const [fetchPicklist] = useLazyGetPicklistQuery();

  async function fetchBaseCatalogs() {
    const [divisions, departments, jobFunctions, seniorities] = await Promise.all([
      fetchEntity({ catalogName: catalogoPersonal.FODivision, select: 'externalCode,name' }, true).unwrap(),
      fetchEntity({ catalogName: catalogoPersonal.FODepartment, select: 'externalCode,name' }, true).unwrap(),
      fetchEntity({ catalogName: catalogoPersonal.FOJobFunction, select: 'externalCode,name' }, true).unwrap(),
      fetchPicklist(
        {
          catalogName: catalogoPersonal.Seniority,
          languages: [catalogoLenguage.ES, catalogoLenguage.externalCode],
        },
        true
      ).unwrap(),
    ]);

    return {
      divisions: divisions as CatalogByCode[],
      departments: departments as CatalogByCode[],
      jobFunctions: jobFunctions as CatalogByCode[],
      seniorities: seniorities as SeniorityOption[],
    };
  }


  async function fetchSpecialities(chapterName: string) {
    if (!chapterName) return [] as CatalogByName[];
    const result = await fetchEntity(
      {
        catalogName: catalogoPersonal.technicalLevel,
        select: 'externalName,externalCode',
        filter: `cust_chapter/name eq '${chapterName.replace(/'/g, "''")}'`,
      },
      true
    ).unwrap();
    return result as CatalogByName[];
  }

  async function fetchTechnologies(specialtyName: string) {
    if (!specialtyName) return [] as CatalogByName[];
    const result = await fetchEntity(
      {
        catalogName: catalogoPersonal.cust_Teconologia,
        select: 'externalName,externalCode',
        filter: `cust_Seniority_Level_Especialidad/externalName eq '${specialtyName.replace(/'/g, "''")}'`,
      },
      true
    ).unwrap();
    return result as CatalogByName[];
  }

  function reset() {
    setStatus('idle');
    setErrorMessage('');
  }

  function fail(message: string) {
    setStatus('error');
    setErrorMessage(message);
    return false;
  }

  /** Escribe el chapter principal. El slot 0 es el chapter principal del candidato. */
  function applyGrowInformation(values: {
    chapter: string;
    idChapterSap: string;
    specialty: string;
    idSpecialtySap: string;
    technology: string;
    idTechnologySap: string;
    technicaLevel: string;
    idTechnicalLevelSap: string;
  }) {
    setValue('growInformation.0.chapter', values.chapter);
    setValue('growInformation.0.idChapterSap', values.idChapterSap);
    setValue('growInformation.0.specialty', values.specialty);
    setValue('growInformation.0.idSpecialtySap', values.idSpecialtySap);
    setValue('growInformation.0.technology', values.technology);
    setValue('growInformation.0.idTechnologySap', values.idTechnologySap);
    setValue('growInformation.0.technicaLevel', values.technicaLevel);
    setValue('growInformation.0.idTechnicalLevelSap', values.idTechnicalLevelSap);
  }

  async function syncPosition(code: string): Promise<boolean> {
    if (!code || !code.trim()) {
      reset();
      return false;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      const positions = await fetchPosition({ code: code.trim() }).unwrap();
      const position = Array.isArray(positions) ? positions[0] : undefined;

      if (!position) return fail(PositionSyncMessages.notFound);
      if (position.effectiveStatus && position.effectiveStatus !== 'A') {
        return fail(PositionSyncMessages.inactive);
      }
      if (position.vacant !== true) return fail(PositionSyncMessages.notVacant);

      const { divisions, departments, jobFunctions, seniorities } = await fetchBaseCatalogs();

      setValue('role', position.externalName_es_ES ?? '');
      setValue('organizationalInformation.division', nameByCode(divisions, position.division));
      setValue('organizationalInformation.department', nameByCode(departments, position.department));

      let chapterCode = '';
      if (position.jobCode) {
        const jobCodes = await fetchJobCode({ jobCode: position.jobCode }).unwrap();
        chapterCode = (Array.isArray(jobCodes) ? jobCodes[0]?.jobFunction : '') ?? '';
      }
      const chapterName = nameByCode(jobFunctions, chapterCode);

      const seniorityLabels = (seniorities ?? []).map((s) => s.label_es_ES ?? '');
      const parsed = parseJobTitle(position.jobTitle ?? '', seniorityLabels);

      const specialities = await fetchSpecialities(chapterName);
      const specialty = matchByName(specialities, parsed.specialty, (i) => i.externalName);

      const technologies = await fetchTechnologies(specialty.label);
      const technology = matchByName(technologies, parsed.technology, (i) => i.externalName);

      const level = matchByName(seniorities, parsed.level, (i) => i.label_es_ES);

      applyGrowInformation({
        chapter: chapterName,
        idChapterSap: chapterCode,
        specialty: specialty.label,
        idSpecialtySap: specialty.code,
        technology: technology.label,
        idTechnologySap: technology.code,
        technicaLevel: level.label,
        idTechnicalLevelSap: level.code,
      });

      setStatus('success');
      return true;
    } catch (error) {
      return fail(PositionSyncMessages.failed);
    }
  }

  return { status, errorMessage, syncPosition, reset };
}
